import React, { useState, useRef, useEffect } from 'react';
import { withImagePlaceholder, IMAGE_PLACEHOLDER_PATH } from '../../utils/imageFallback';

const LazyImage = ({
  src,
  alt = '',
  className = '',
  placeholder = null,
  onLoad = null,
  onError = null,
  objectFit = 'cover',
  showProgress = true,
  ...props
}) => {
  const resolvedSrc = withImagePlaceholder(src);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);
  const progressIntervalRef = useRef(null);

  useEffect(() => {
    // Reset states when src changes
    setLoading(true);
    setError(false);
    setProgress(0);
    setImageLoaded(false);

    // Simulate progress for better UX (since we can't always track real progress due to CORS)
    if (showProgress) {
      let currentProgress = 0;
      progressIntervalRef.current = setInterval(() => {
        if (currentProgress < 90) {
          currentProgress += Math.random() * 15; // Increment by random amount
          currentProgress = Math.min(currentProgress, 90); // Cap at 90%
          setProgress(currentProgress);
        }
      }, 100);
    }

    // Create a new image to preload
    const img = new Image();
    imgRef.current = img;

    img.onload = () => {
      // Clear progress interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setProgress(100);
      setImageLoaded(true);
      // Small delay to show 100% before hiding loader
      setTimeout(() => {
        setLoading(false);
        if (onLoad) onLoad();
      }, 150);
    };

    img.onerror = () => {
      // Clear progress interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setError(true);
      setLoading(false);
      if (onError) onError();
    };

    // Start loading the image
    img.src = resolvedSrc;

    // Cleanup
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [resolvedSrc, showProgress, onLoad, onError]);

  // Default placeholder (skeleton)
  const defaultPlaceholder = (
    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse">
      <div className="absolute inset-0 flex items-center justify-center">
        <svg
          className="w-8 h-8 text-gray-400 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    </div>
  );

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ minHeight: '100px' }}>
      {/* Loading Placeholder */}
      {loading && !error && (
        <div className="absolute inset-0 z-10">
          {placeholder || defaultPlaceholder}
          {/* Progress Bar */}
          {showProgress && progress > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
              <div
                className="h-full bg-blue-500 transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <img
          src={IMAGE_PLACEHOLDER_PATH}
          alt={alt || 'Placeholder'}
          className="w-full h-full"
          style={{ objectFit }}
        />
      )}

      {/* Actual Image */}
      {resolvedSrc && (
        <img
          src={resolvedSrc}
          alt={alt}
          className={`w-full h-full transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ objectFit }}
          {...props}
        />
      )}
    </div>
  );
};

export default LazyImage;

